// AGC: PermissionBlockedModal - Shown when a device permission is permanently blocked.
// AGC: Provides a clear message and a deep-link button to open the app's Android/iOS
// AGC: Settings page so the user can manually re-enable the permission.
// AGC: Uses permissionHelper.openAppSettings() which calls capacitor-native-settings.

import React from 'react'
import { MapPin, Camera, Mic, Settings, X } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { openAppSettings } from '../../services/permissionHelper'
import { Bell } from 'lucide-react'
import type { PermissionType } from '../../services/permissionHelper'

// AGC: Extended type that includes 'notifications' for push permission
export type ExtendedPermissionType = PermissionType | 'notifications'

interface PermissionBlockedModalProps {
  isOpen: boolean
  // AGC: Which permission is blocked -- drives icon, title, and message
  permissionType: ExtendedPermissionType
  onClose: () => void
}

// AGC: Icon + color config per permission type (extended to include notifications)
const PERMISSION_CONFIG: Record<
  ExtendedPermissionType,
  { icon: React.FC<{ className?: string }>; color: string; bgColor: string }
> = {
  location: {
    icon: ({ className }) => <MapPin className={className} />,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  camera: {
    icon: ({ className }) => <Camera className={className} />,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
  microphone: {
    icon: ({ className }) => <Mic className={className} />,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  },
  notifications: {
    icon: ({ className }) => <Bell className={className} />,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
}

/**
 * AGC: PermissionBlockedModal - Reusable modal for blocked device permissions.
 * AGC: Shows which permission is blocked, explains how to fix it,
 * AGC: and provides a button to open app settings.
 */
export const PermissionBlockedModal: React.FC<PermissionBlockedModalProps> = ({
  isOpen,
  permissionType,
  onClose,
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  const config = PERMISSION_CONFIG[permissionType]
  const Icon = config.icon

  // AGC: Fetch permission-specific message from locale files
  const messageKey = `nav.devicePermissions.${permissionType}.blockedMessage`
  const message = t(messageKey as any)

  const handleOpenSettings = async () => {
    await openAppSettings()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* AGC: Close button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Icon + title */}
        <div className="px-6 pb-4 flex flex-col items-center text-center">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${config.bgColor}`}
          >
            <Icon className={`w-8 h-8 ${config.color}`} />
          </div>

          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
            {t('nav.devicePermissions.blockedTitle' as any)}
          </h2>

          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {message}
          </p>
        </div>

        {/* AGC: Action buttons */}
        <div className="px-6 pb-6 flex flex-col gap-3">
          <button
            onClick={handleOpenSettings}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0B5F4A] hover:bg-[#094935] text-white rounded-xl font-medium transition-colors"
          >
            <Settings className="w-5 h-5" />
            {t('nav.devicePermissions.openSettings' as any)}
          </button>

          <button
            onClick={onClose}
            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {t('nav.devicePermissions.cancel' as any)}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PermissionBlockedModal
